import { useQuery } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import { authService, powerService } from '@/services';
import { useAuthStore } from '@/stores';
import { fetchFTTHToken, decodeJWT, apiClient } from '@/services/api-client';
import { queryClient } from '@/services/query-client';
import { stopNotificationPolling } from '@/stores/notificationStore';
import { supabase } from '@/utils/supabase';
import axios from 'axios';

// ===== Real FTTH Login Hook =====
export const useLogin = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { setUser, setTokens, setPartnerId, setSavedCredentials, setRememberMe, setSessionId } = useAuthStore();

  const login = useCallback(
    async (username: string, password: string, rememberMe = false) => {
      setIsLoading(true);
      setError(null);

      const cleanUsername = username.trim();
      const rawPassword = password;

      if (!cleanUsername || !rawPassword) {
        setError('يرجى إدخال اسم المستخدم وكلمة المرور');
        setIsLoading(false);
        return false;
      }

      try {
        // Step 1: Fetch OAuth2 token from FTTH server
        const tokenData = await fetchFTTHToken(cleanUsername, rawPassword);

        // Step 2: Detect account switch — if the stored user differs from the incoming
        // username, wipe ALL cached data to prevent cross-account leakage.
        const prevUsername = useAuthStore.getState().user?.username;
        const incomingUsername = cleanUsername.toLowerCase();
        const isAccountSwitch = prevUsername && prevUsername.toLowerCase() !== incomingUsername;

        if (isAccountSwitch || !prevUsername) {
          // Clear React Query cache (ticket list, notification logs, customers, etc.)
          queryClient.clear();
          // Stop any background notification polling from the previous session
          stopNotificationPolling();
          console.warn(`[Login] Account switched from "${prevUsername}" to "${incomingUsername}". All cached data cleared.`);
        }

        // Also clear server-side cache for this new token (fire-and-forget)
        // Use apiClient.post to comply with AppDeploy's URL routing requirements
        apiClient.post('/auth/clear-session-cache', { ftth_token: tokenData.access_token })
          .catch((_e) => { /* Non-critical — continue even if server cache clear fails */ });

        // Step 3: Decode JWT to extract user info and partnerId
        const decoded = decodeJWT(tokenData.access_token);

        let partnerId: number | null = null;
        let roles: string[] = [];
        let groups: string[] = [];

        if (decoded) {
          // AccountId is used as partnerId in API URLs (e.g. /partners/5922/...)
          partnerId = decoded['AccountId'] || decoded['accountId'] || null;
          roles = decoded['roles'] || decoded['realm_access']?.roles || [];
          groups = decoded['Groups'] || decoded['groups'] || [];
        }

        // Step 4: Store tokens and credentials for auto-refresh
        setTokens(tokenData.access_token, tokenData.refresh_token || '');
        setSavedCredentials(cleanUsername);
        setRememberMe(rememberMe);

        // Step 5: Set partner ID
        if (partnerId) {
          setPartnerId(partnerId);
        }

        // Step 6: Extract authenticated FTTH Nexus email
        let authenticatedEmail = (decoded?.['email'] && !decoded['email'].endsWith('@ftth.iq')) ? decoded['email'] : '';
        if (!authenticatedEmail) {
          try {
            const { data: supaSession } = await supabase.auth.getSession();
            if (supaSession?.session?.user?.email) {
              authenticatedEmail = supaSession.session.user.email;
            }
          } catch {}
        }
        if (!authenticatedEmail && typeof window !== 'undefined') {
          const storedEmail = localStorage.getItem('google_user_email');
          if (storedEmail) {
            authenticatedEmail = storedEmail;
          }
        }

        setUser({
          id: String(partnerId || cleanUsername),
          username: decoded?.['preferred_username'] || decoded?.['Username'] || cleanUsername,
          email: authenticatedEmail,
          firstName: decoded?.['given_name'] || decoded?.['name'] || cleanUsername,
          lastName: '',
          roles,
          accountId: partnerId || undefined,
          partnerId: partnerId || undefined,
          groups,
          authType: 'SSO',
          isOwner: true,
          forcePasswordChange: false,
        });

        // Step 7: Generate a fresh session ID to invalidate any stale cached data from prior sessions
        const newSessionId = typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        setSessionId(newSessionId);

        return true;
      } catch (err: any) {
        const msg = err?.response?.data?.error || err?.message || 'فشل الاتصال بالخادم';
        setError(msg);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [setUser, setTokens, setPartnerId, setSavedCredentials, setRememberMe, setSessionId],
  );

  const loginSubUser = useCallback(
    async (partnerId: string, username: string, password: string, rememberMe = false) => {
      setIsLoading(true);
      setError(null);

      const cleanPartnerId = partnerId.trim();
      const cleanUsername = username.trim();
      const rawPassword = password;

      if (!cleanPartnerId || !cleanUsername || !rawPassword) {
        setError('يرجى إدخال معرف المقاول واسم المستخدم وكلمة المرور');
        setIsLoading(false);
        return { success: false, forcePasswordChange: false };
      }

      try {
        const response = await axios.post('/api/auth/sub-user-login', {
          external_partner_id: cleanPartnerId,
          partnerId: cleanPartnerId,
          username: cleanUsername,
          password: rawPassword,
        }, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const data = response.data;
        if (!data || !data.token || !data.user) {
          throw new Error('اسم المستخدم أو كلمة المرور أو معرف المقاول غير صحيح');
        }

        // Account switch detection
        const prevUsername = useAuthStore.getState().user?.username;
        const incomingUsername = cleanUsername.toLowerCase();
        const isAccountSwitch = prevUsername && prevUsername.toLowerCase() !== incomingUsername;

        if (isAccountSwitch || !prevUsername) {
          queryClient.clear();
          stopNotificationPolling();
        }

        const user = data.user;
        const roles = user.roles || (user.role ? [user.role] : ['contractor_user']);
        const permissions = data.permissions || [];

        // Store tokens and credentials (non-sensitive identifiers only)
        setTokens(data.token, '');
        setSavedCredentials(cleanUsername, cleanPartnerId);
        setRememberMe(rememberMe);
        setPartnerId(user.partnerId || cleanPartnerId);

        let subUserEmail = (user.email && !user.email.endsWith('@ftth.iq')) ? user.email : '';
        if (!subUserEmail) {
          try {
            const { data: supaSession } = await supabase.auth.getSession();
            if (supaSession?.session?.user?.email) {
              subUserEmail = supaSession.session.user.email;
            }
          } catch {}
        }
        if (!subUserEmail && typeof window !== 'undefined') {
          const storedEmail = localStorage.getItem('google_user_email');
          if (storedEmail) {
            subUserEmail = storedEmail;
          }
        }

        setUser({
          id: user.id,
          username: user.username,
          email: subUserEmail,
          firstName: user.fullName || user.displayName || user.username,
          lastName: '',
          roles,
          accountId: user.partnerId || cleanPartnerId,
          partnerId: user.partnerId || cleanPartnerId,
          groups: [],
          authType: 'LOCAL',
          permissions,
          forcePasswordChange: !!user.forcePasswordChange,
          isOwner: roles.includes('contractor_admin') || !!user.isOwner,
          role: roles[0],
        });

        const newSessionId = typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        setSessionId(newSessionId);

        return { success: true, forcePasswordChange: !!user.forcePasswordChange, token: data.token };
      } catch (err: any) {
        const msg = err?.response?.data?.error || err?.message || 'اسم المستخدم أو كلمة المرور أو معرف المقاول غير صحيح';
        setError(msg);
        return { success: false, forcePasswordChange: false };
      } finally {
        setIsLoading(false);
      }
    },
    [setUser, setTokens, setPartnerId, setSavedCredentials, setRememberMe, setSessionId],
  );

  return { login, loginSubUser, isLoading, error, setError };
};

// ===== Change Password Hook =====
export const useChangePassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changePassword = useCallback(async (newPassword: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const { accessToken } = useAuthStore.getState();
      const response = await axios.post('/api/auth/change-password', {
        newPassword,
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.data?.success) {
        const currentUser = useAuthStore.getState().user;
        if (currentUser) {
          useAuthStore.getState().setUser({
            ...currentUser,
            forcePasswordChange: false,
          });
        }
        return true;
      }
      throw new Error(response.data?.error || 'فشل تغيير كلمة المرور');
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'فشل تغيير كلمة المرور');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { changePassword, isLoading, error };
};


// ===== Current User Hook =====
export const useCurrentUser = () => {
  return useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      try {
        const response = await authService.getCurrentUser();
        return response.data;
      } catch (_e) {
        return null;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
};

// ===== Logout Hook =====
export const useLogout = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { logout, setLogoutConfirmOpen } = useAuthStore();

  const requestLogout = useCallback(() => {
    setLogoutConfirmOpen(true);
  }, [setLogoutConfirmOpen]);

  const handleLogout = useCallback(async () => {
    setIsLoading(true);
    try {
      // Parallel server-side session invalidation with safety timeout
      // Guarantees that worker timeouts, network drops, or upstream failures NEVER block local FTTH Nexus logout
      await Promise.race([
        Promise.allSettled([
          apiClient.post('/auth/logout').catch(() => {}),
          apiClient.post('/auth/clear-session-cache').catch(() => {}),
          authService.logout().catch(() => {}),
          supabase.auth.signOut().catch((err) => {
            console.warn('[Logout] Supabase signOut error:', err);
          }),
        ]),
        new Promise((resolve) => setTimeout(resolve, 2500)),
      ]);
    } catch (_e) {
      // Ignore errors on logout
    } finally {
      queryClient.clear(); // Clear React Query cache to prevent data leakage between sessions!
      powerService.invalidateCache();
      stopNotificationPolling();
      logout();
      try {
        localStorage.removeItem('google_auth_success');
        localStorage.removeItem('google_user_email');
        localStorage.removeItem('google_user_name');
        sessionStorage.removeItem('ftth_push_banner_dismissed');
        sessionStorage.removeItem('ftth_session_id');
        sessionStorage.removeItem('portal_token');
        sessionStorage.removeItem('portal_session');
        sessionStorage.removeItem('oauth_state');
      } catch (e) {}
      setIsLoading(false);
      window.location.hash = '#/login';
      window.location.reload();
    }
  }, [logout]);


  return { 
    logout: requestLogout, 
    confirmLogout: handleLogout, 
    isLoading 
  };
};
